import React, { useEffect, useState } from "react";
import axios from "axios";
import InvoiceModal from "../InvoiceModal";
import ViewInvoiceModal from "../ViewInvoiceModal";
import SkeletonLoader from "../../../resuableComponents/SkeletonLoader";
import { jsPDF } from "jspdf";
import headerImage from "../../../../Images/header-logo.png";
import footerImage from "../../../../Images/footer-logo.png";
import CustomDatePicker from "../../../resuableComponents/CustomDatePicker";
import CustomDropdown from "../../../resuableComponents/CustomDropdown";
import ConfirmationModal from "../../../resuableComponents/ConfirmationModal";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  FaHospital,
  FaCheckCircle,
  FaUser,
  FaCalendarAlt,
  FaPhone,
  FaDoorOpen,
  FaBed,
  FaFileAlt,
  FaTrashAlt
} from "react-icons/fa";
import "./IpdAll.css";

function IpdList({ patientId }) {
  const [loading, setLoading] = useState(false);
  const [dischargedRecords, setDischargedRecords] = useState([]);
  const authData = JSON.parse(localStorage.getItem("authData"));
  const token = authData?.accessToken;

  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);

  const [isViewInvoiceModalOpen, setIsViewInvoiceModalOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);

  const openInvoiceModal = async (consultation, isIPD = false, billType = "treatment") => {
    console.log("Opening invoice modal for consultation:", consultation);
    let updatedConsultation = { ...consultation, isIPD };
    setSelectedBillType(billType);

    if (isIPD) {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}get-all-treatment?admissionId=${consultation._id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const treatmentsData = res.data.result || [];
        if (treatmentsData.length > 0) {
          // Aggregate all treatments and medicines from all observation records
          const allTreatments = [];
          const allMedicines = [];

          treatmentsData.forEach(item => {
            if (item.treatments && Array.isArray(item.treatments)) {
              allTreatments.push(...item.treatments);
            }
            if (item.medicines && Array.isArray(item.medicines)) {
              allMedicines.push(...item.medicines);
            }
          });

          updatedConsultation.treatments = allTreatments;
          updatedConsultation.medicines = allMedicines;
        }
      } catch (err) {
        console.error("Error fetching treatment data for modal:", err);
      }
    }

    setSelectedConsultation(updatedConsultation);
    setIsInvoiceModalOpen(true);
  };

  const closeInvoiceModal = () => {
    setIsInvoiceModalOpen(false);
    setSelectedConsultation(null);
  };

  const [selectedBillType, setSelectedBillType] = useState("treatment");
  const [isSelectedIPD, setIsSelectedIPD] = useState(false);

  // Discharge Summary states
  const [showDischargeSummaryModal, setShowDischargeSummaryModal] =
    useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dischargeType, setDischargeType] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [patientAddress, setPatientAddress] = useState("");
  const [patientHistory, setPatientHistory] = useState("");
  const [clinicalSummary, setClinicalSummary] = useState("");
  const [investigations, setInvestigations] = useState("");
  const [treatmentReview, setTreatmentReview] = useState("");
  const [treatmentAdvice, setTreatmentAdvice] = useState("");
  const [remarks, setRemarks] = useState("");
  const [dischargeFollowUpDate, setDischargeFollowUpDate] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [consultantName, setConsultantName] = useState("");
  const [diagnoses, setDiagnoses] = useState([]);
  const [diagnosisText, setDiagnosisText] = useState("");
  const [treatmentsData, setTreatmentsData] = useState([]);
  const [loadingDischargeCard, setLoadingDischargeCard] = useState(false);
  const [originalDischargeCardData, setOriginalDischargeCardData] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6; // Step 1: Details, Steps 2-5: Textareas, Step 6: Remarks & Follow-up

  const openViewInvoiceModal = (appointmentId, isIPD = false, billType) => {
    setSelectedAppointmentId(appointmentId);
    setIsSelectedIPD(isIPD);
    setSelectedBillType(billType || null);
    setIsViewInvoiceModalOpen(true);
  };

  const fetchDischargeCard = async (admissionId) => {
    try {
      setLoadingDischargeCard(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}get-discharge-card/${admissionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const dischargeCard = res.data.result || res.data;

      // Store original data for comparison
      setOriginalDischargeCardData(dischargeCard);

      // Fetch invoice to get treatments for treatment review
      let treatmentReviewFromInvoice = "";
      try {
        const [invoiceRes, treatmentsRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}get-ipd-invoice?admissionId=${admissionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL}get-all-treatment?admissionId=${admissionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        ]);

        const invoiceData = invoiceRes.data.invoice;
        const treatmentsData = treatmentsRes.data.result || [];

        // Extract treatments from both sources
        const treatmentNamesSet = new Set();

        // From get-all-treatment (daily records)
        treatmentsData.forEach(item => {
          if (item.treatments && Array.isArray(item.treatments)) {
            item.treatments.forEach(t => {
              if (t.name) treatmentNamesSet.add(t.name);
            });
          }
        });

        // From invoice
        if (invoiceData && invoiceData.treatments && Array.isArray(invoiceData.treatments)) {
          invoiceData.treatments.forEach(t => {
            if (t.name) treatmentNamesSet.add(t.name);
          });
        }

        const treatmentsToUse = Array.from(treatmentNamesSet).map(name => ({ name }));

        if (treatmentsToUse.length > 0) {
          treatmentReviewFromInvoice = "Treatment provided during admission:\n\n";
          treatmentsToUse.forEach((treatment, index) => {
            const treatmentName = treatment.name ? treatment.name.replace(/-/g, " ") : "";
            if (treatmentName.trim()) {
              treatmentReviewFromInvoice += `${index + 1}) ${treatmentName} -\n`;
            }
          });
          treatmentReviewFromInvoice += "\nResponse to Treatment:\n- ";
        }
      } catch (invoiceError) {
        console.error("Error fetching data for treatments:", invoiceError);
        // If fetch fails, use saved treatment review or default
        treatmentReviewFromInvoice = dischargeCard?.treatmentReview || getDefaultTreatmentReview();
      }

      // Populate form fields with fetched data
      // Use saved data if available, otherwise use default templates for empty fields
      if (dischargeCard) {
        setDischargeType(dischargeCard.dischargeType || "");
        setRegNumber(dischargeCard.regNumber || "");
        setPatientAddress(dischargeCard.patientAddress || "");
        setPatientHistory(dischargeCard.patientHistory || "");
        // Use saved data if exists, otherwise use default templates
        setClinicalSummary(dischargeCard.clinicalSummary || getDefaultClinicalSummary());
        setInvestigations(dischargeCard.investigations || getDefaultInvestigations());
        // Use treatment review from invoice (with treatments), or saved one if invoice fetch failed
        setTreatmentReview(treatmentReviewFromInvoice || dischargeCard.treatmentReview || getDefaultTreatmentReview());
        setTreatmentAdvice(dischargeCard.treatmentAdvice || getDefaultTreatmentAdvice());
        setRemarks(dischargeCard.remarks || getDefaultRemarks());

        // Handle consultants array
        if (dischargeCard.consultants && Array.isArray(dischargeCard.consultants)) {
          console.log('Setting consultants:', dischargeCard.consultants);
          setConsultants(dischargeCard.consultants);
        } else {
          setConsultants([]);
        }

        // Handle diagnoses array
        if (dischargeCard.diagnoses && Array.isArray(dischargeCard.diagnoses)) {
          console.log('Setting diagnoses:', dischargeCard.diagnoses);
          setDiagnoses(dischargeCard.diagnoses);
        } else {
          setDiagnoses([]);
        }

        // Handle follow-up date - convert ISO string to dayjs object
        if (dischargeCard.followUpDate) {
          // Parse the ISO string and ensure it's a valid dayjs object
          let followUpDate;
          if (typeof dischargeCard.followUpDate === 'string') {
            // Handle ISO string format like "2025-12-28T18:30:00.000Z"
            followUpDate = dayjs(dischargeCard.followUpDate);
          } else if (dischargeCard.followUpDate instanceof Date) {
            followUpDate = dayjs(dischargeCard.followUpDate);
          } else if (dayjs.isDayjs(dischargeCard.followUpDate)) {
            followUpDate = dischargeCard.followUpDate;
          } else {
            followUpDate = dayjs(dischargeCard.followUpDate);
          }

          if (followUpDate && followUpDate.isValid()) {
            setDischargeFollowUpDate(followUpDate);
          } else {
            console.warn('Invalid follow-up date:', dischargeCard.followUpDate);
            setDischargeFollowUpDate(null);
          }
        } else {
          setDischargeFollowUpDate(null);
        }
      }
    } catch (error) {
      console.error("Error fetching discharge card:", error);
      toast.error(
        error.response?.data?.message || "Failed to load discharge card"
      );
    } finally {
      setLoadingDischargeCard(false);
    }
  };

  const openDischargeSummaryModal = async (record) => {
    setSelectedRecord(record);
    setCurrentStep(1); // Reset to first step

    // Reset form first
    setDischargeType("");
    setRegNumber(record.patient.patientId || "");
    setPatientAddress(record.patient.address || "");

    // If discharge card already exists, fetch and populate the data
    // Otherwise, set default templates for new records with actual data
    if (record.isDischargeCardGenerated) {
      // Will be populated by fetchDischargeCard
      setClinicalSummary("");
      setInvestigations("");
      setTreatmentReview("");
      setTreatmentAdvice("");
      setRemarks("");
      setDischargeFollowUpDate(null); // Reset first, will be set by fetchDischargeCard
      setConsultants([]); // Reset first, will be set by fetchDischargeCard
      setDiagnoses([]); // Reset first, will be set by fetchDischargeCard
      await fetchDischargeCard(record._id);
    } else {
      // Set default templates for new records, with clinical summary and treatment review populated from invoice data
      const templates = await fetchInvoiceAndGenerateTemplates(record);
      setClinicalSummary(templates.clinicalSummary);
      setInvestigations(getDefaultInvestigations());
      setTreatmentReview(templates.treatmentReview);
      setTreatmentAdvice(getDefaultTreatmentAdvice());
      setRemarks(getDefaultRemarks());
      setDischargeFollowUpDate(null);
      setConsultants([]);
      setDiagnoses([]);
    }

    setConsultantName("");
    setDiagnosisText("");

    setShowDischargeSummaryModal(true);

    // Fetch treatments for this admission
    fetchTreatmentsForRecord(record._id);
  };

  const fetchTreatmentsForRecord = async (admissionId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}get-all-treatment?admissionId=${admissionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTreatmentsData(res.data.result || []);
    } catch (err) {
      console.error("Error fetching treatments:", err);
      setTreatmentsData([]);
    }
  };

  // Default templates for textareas
  const getDefaultInvestigations = () => {
    return `1.Blood investigations 
CBC :
HB  :
RBC :
WBC :
Platelates :
Urine :
Blood Sugar : __ F
                         
Kidney function :
Liver function  :
Lipid profile  :
Vit B12  :
Vit D  :
Calcium level :
Others :


2.Xray
Cervical spine

Thoracic lumber 

Lumbo sacral

Knee joint

Others 


3.MRI :
Cervical spine

Lumbo sacral spine

Knee joint

Shoulder joint 

Hip joint 

Others :

4. Endoscopy :


5.others`;
  };

  const getDefaultClinicalSummary = () => {
    return `A Patient named___ age__M/F was suffering from _____ since last____days/ months/years 

He /she was under ____ treatment for __ from last __ days/months

He /she was advised Ayurvedic panchkarma treatment for ___ from ___ to ___ and follow up for next __ Days/ months

Patient presented with the following symptoms and clinical findings:

History:
- 

Clinical Examination:
- 

Vital Signs:
- BP: 
- Pulse: 
- Temperature: 
- Respiratory Rate: 

General Examination:
- 

Systemic Examination:
- 

Assessment:
- `;
  };

  // Fetch invoice data and generate populated templates
  const fetchInvoiceAndGenerateTemplates = async (record) => {
    try {
      // Fetch treatments and observations from all recorded treatment sessions
      const treatmentsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}get-all-treatment?admissionId=${record._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const treatmentsData = treatmentsRes.data.result || [];

      let observations = {
        bp: "",
        pulse: "",
        temperature: "",
        oxygen: "",
        weight: ""
      };

      let actualTreatments = [];
      let actualMedicines = [];

      if (treatmentsData.length > 0) {
        // Extract all treatment and medicine names
        const treatmentNamesSet = new Set();
        const medicineNamesSet = new Set();

        treatmentsData.forEach(item => {
          if (item.treatments && Array.isArray(item.treatments)) {
            item.treatments.forEach(t => {
              if (t.name) treatmentNamesSet.add(t.name);
            });
          }
          if (item.medicines && Array.isArray(item.medicines)) {
            item.medicines.forEach(m => {
              if (m.name) medicineNamesSet.add(m.name);
            });
          }
        });

        actualTreatments = Array.from(treatmentNamesSet).map(name => ({ name }));
        actualMedicines = Array.from(medicineNamesSet).map(name => ({ name }));

        // Get the latest observation for vital signs
        const latestTreatment = treatmentsData[treatmentsData.length - 1];
        if (latestTreatment.observations) {
          observations = {
            bp: latestTreatment.observations.bp || "",
            pulse: latestTreatment.observations.pulse || "",
            temperature: latestTreatment.observations.temperature || "",
            oxygen: latestTreatment.observations.oxygen || "",
            weight: latestTreatment.observations.weight || ""
          };
        }
      }

      const admission = record; // Use record directly as it contains latest admission info
      const patient = admission.patient;

      // Get patient details from admission record
      const patientName = patient?.name || "___";
      const patientAge = patient?.age || "___";
      const patientGender = patient?.gender || "";
      const genderText = patientGender.toLowerCase().startsWith("f") ? "F" : patientGender.toLowerCase().startsWith("m") ? "M" : "M/F";
      const pronoun = patientGender.toLowerCase().startsWith("f") ? "She" : patientGender.toLowerCase().startsWith("m") ? "He" : "He/She";

      // Get reason for admission
      const reason = admission.reason || "_____";

      // Calculate days of admission
      const admissionDate = admission.admissionDate ? dayjs(admission.admissionDate) : null;
      const dischargeDate = admission.dischargedDate ? dayjs(admission.dischargedDate, "DD-MM-YYYY") : dayjs();
      const totalDays = admissionDate && dischargeDate ? dischargeDate.diff(admissionDate, 'day') : 0;

      // Final lists to use
      const treatmentsToUse = actualTreatments;
      // medicinesToUse is available for future template updates if needed

      const treatmentsList = treatmentsToUse.length > 0
        ? treatmentsToUse.map(t => t.name).filter(name => name && name.trim()).join(", ")
        : "___";

      // IPD Summary focuses on treatments and medications
      const servicesList = "";

      // Combine treatments and services (only if services exist and are not just numbers)
      const allTreatments = treatmentsList + (servicesList ? (treatmentsList !== "___" ? `, ${servicesList}` : servicesList) : "");

      // Format dates
      const admissionDateStr = admissionDate ? admissionDate.format("DD/MM/YYYY") : "___";
      const dischargeDateStr = dischargeDate ? dischargeDate.format("DD/MM/YYYY") : "___";

      // Generate the clinical summary with actual data
      const clinicalSummary = `A Patient named ${patientName} age ${patientAge} ${genderText} was suffering from ${reason} since last___days/ months/years 

${pronoun} was under ${allTreatments || "___"} treatment for ${reason} from last ${totalDays > 0 ? totalDays : "___"} days/months

${pronoun} was advised Ayurvedic panchkarma treatment for ${allTreatments || "___"} from ${admissionDateStr} to ${dischargeDateStr} and follow up for next __ Days/ months

Patient presented with the following symptoms and clinical findings:

History:
- 

Clinical Examination:
- 

Vital Signs:
- BP: ${observations.bp || ""}
- Pulse: ${observations.pulse || ""}
- Temperature: ${observations.temperature ? `${observations.temperature}°F` : ""}
- Oxygen: ${observations.oxygen ? `${observations.oxygen}%` : ""}
- Weight: ${observations.weight ? `${observations.weight} kg` : ""}
- Respiratory Rate: 

General Examination:
- 

Systemic Examination:
- 

Assessment:
- `;

      // Generate treatment review with actual treatments
      let treatmentReview = "Treatment provided during admission:\n\n";

      if (treatmentsToUse.length > 0) {
        // Format treatments with numbering, replace "-" with space, and add "-" for notes
        treatmentsToUse.forEach((treatment, index) => {
          const treatmentName = treatment.name ? treatment.name.replace(/-/g, " ") : "";
          if (treatmentName.trim()) {
            treatmentReview += `${index + 1}) ${treatmentName} -\n`;
          }
        });
      } else {
        treatmentReview += "1) -\n";
      }

      treatmentReview += "\nResponse to Treatment:\n- ";

      return {
        clinicalSummary,
        treatmentReview
      };
    } catch (error) {
      console.error("Error fetching treatment data for templates:", error);
      // Return default templates if fetch fails
      return {
        clinicalSummary: getDefaultClinicalSummary(),
        treatmentReview: getDefaultTreatmentReview()
      };
    }
  };

  const getDefaultTreatmentReview = () => {
    return `Treatment provided during admission:

1) -

Response to Treatment:
- `;
  };

  const getDefaultTreatmentAdvice = () => {
    return `Post-discharge treatment advice:

1. Medications:
- 

2. Diet:
- 

3. Lifestyle modifications:
- 

4. Follow-up:
- 

5. Precautions:
- `;
  };

  const getDefaultRemarks = () => {
    return `Additional remarks:

- 

- 

- `;
  };

  const resetDischargeSummaryForm = () => {
    setDischargeType("");
    setRegNumber("");
    setPatientAddress("");
    setPatientHistory("");
    setClinicalSummary("");
    setInvestigations("");
    setTreatmentReview("");
    setTreatmentAdvice("");
    setRemarks("");
    setDischargeFollowUpDate(null);
    setConsultants([]);
    setConsultantName("");
    setDiagnoses([]);
    setDiagnosisText("");
    setTreatmentsData([]);
    setSelectedRecord(null);
    setLoadingDischargeCard(false);
    setOriginalDischargeCardData(null);
    setCurrentStep(1); // Reset to first step
  };

  // Step navigation functions
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Check if form data has changed from original
  const hasFormDataChanged = () => {
    if (!originalDischargeCardData) return true; // New record, always consider changed

    const formatFollowUpDate = (dateValue) => {
      if (!dateValue) return null;
      if (dayjs.isDayjs(dateValue)) {
        return dateValue.isValid() ? dateValue.toISOString() : null;
      }
      if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue.toISOString();
      }
      const parsed = dayjs(dateValue);
      return parsed.isValid() ? parsed.toISOString() : null;
    };

    const currentFollowUpDate = formatFollowUpDate(dischargeFollowUpDate);
    const originalFollowUpDate = originalDischargeCardData.followUpDate
      ? dayjs(originalDischargeCardData.followUpDate).toISOString()
      : null;

    return (
      (dischargeType || "normal") !== (originalDischargeCardData.dischargeType || "normal") ||
      (regNumber || "") !== (originalDischargeCardData.regNumber || "") ||
      (patientAddress || "") !== (originalDischargeCardData.patientAddress || "") ||
      (patientHistory || "") !== (originalDischargeCardData.patientHistory || "") ||
      (clinicalSummary || "") !== (originalDischargeCardData.clinicalSummary || "") ||
      (investigations || "") !== (originalDischargeCardData.investigations || "") ||
      (treatmentReview || "") !== (originalDischargeCardData.treatmentReview || "") ||
      (treatmentAdvice || "") !== (originalDischargeCardData.treatmentAdvice || "") ||
      (remarks || "") !== (originalDischargeCardData.remarks || "") ||
      JSON.stringify(consultants) !== JSON.stringify(originalDischargeCardData.consultants || []) ||
      JSON.stringify(diagnoses) !== JSON.stringify(originalDischargeCardData.diagnoses || []) ||
      currentFollowUpDate !== originalFollowUpDate
    );
  };

  // Update Discharge Summary API Call
  const updateDischargeSummary = async (record) => {
    if (!record || !record._id) {
      toast.error("Invalid record selected");
      return;
    }

    // Validate and format follow-up date - send as ISO string or null
    const formatFollowUpDate = (dateValue) => {
      if (!dateValue) return null;
      // If it's a dayjs object, convert to ISO string
      if (dayjs.isDayjs(dateValue)) {
        return dateValue.isValid() ? dateValue.toISOString() : null;
      }
      // If it's a Date object, convert to ISO string
      if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue.toISOString();
      }
      // If it's a string, try to parse it
      const parsed = dayjs(dateValue);
      return parsed.isValid() ? parsed.toISOString() : null;
    };

    const dischargeSummaryData = {
      dischargeType: dischargeType || "normal",
      regNumber: regNumber || record.patient.patientId || "",
      patientAddress: patientAddress || record.patient.address || "",
      patientHistory: patientHistory || "",
      consultants: consultants,
      diagnoses: diagnoses,
      clinicalSummary: clinicalSummary || "",
      investigations: investigations || "",
      treatmentReview: treatmentReview || "",
      treatmentAdvice: treatmentAdvice || "",
      remarks: remarks || "",
      followUpDate: formatFollowUpDate(dischargeFollowUpDate),
    };

    try {
      setLoading(true);
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}update-discharge-card/${record._id}`,
        dischargeSummaryData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(res.data.message || "Discharge summary updated successfully!");
      return res.data;
    } catch (error) {
      console.error("Error updating discharge summary:", error);
      toast.error(
        error.response?.data?.message || "Failed to update discharge summary"
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Save Discharge Summary API Call (Create new)
  const saveDischargeSummary = async (record) => {
    if (!record || !record._id) {
      toast.error("Invalid record selected");
      return;
    }

    // Validate and format follow-up date - send as ISO string or null
    const formatFollowUpDate = (dateValue) => {
      if (!dateValue) return null;
      // If it's a dayjs object, convert to ISO string
      if (dayjs.isDayjs(dateValue)) {
        return dateValue.isValid() ? dateValue.toISOString() : null;
      }
      // If it's a Date object, convert to ISO string
      if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue.toISOString();
      }
      // If it's a string, try to parse it
      const parsed = dayjs(dateValue);
      return parsed.isValid() ? parsed.toISOString() : null;
    };

    const dischargeSummaryData = {
      admissionId: record._id,
      dischargeType: dischargeType || "normal",
      regNumber: regNumber || record.patient.patientId || "",
      patientAddress: patientAddress || record.patient.address || "",
      patientHistory: patientHistory || "",
      consultants: consultants,
      diagnoses: diagnoses,
      clinicalSummary: clinicalSummary || "",
      investigations: investigations || "",
      treatmentReview: treatmentReview || "",
      treatmentAdvice: treatmentAdvice || "",
      remarks: remarks || "",
      followUpDate: formatFollowUpDate(dischargeFollowUpDate),
    };

    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}generate-discharge-card`,
        dischargeSummaryData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(res.data.message || "Discharge summary saved successfully!");
      return res.data;
    } catch (error) {
      console.error("Error saving discharge summary:", error);
      toast.error(
        error.response?.data?.message || "Failed to save discharge summary"
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Save or Update Discharge Summary (handles both create and update)
  const saveOrUpdateDischargeSummary = async (record) => {
    if (record.isDischargeCardGenerated) {
      return await updateDischargeSummary(record);
    } else {
      return await saveDischargeSummary(record);
    }
  };

  // Delete Discharge Summary API Call
  const deleteDischargeSummary = async (admissionId) => {
    try {
      setLoading(true);
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}delete-discharge-card/${admissionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(res.data.message || "Discharge summary deleted successfully!");
      return res.data;
    } catch (error) {
      console.error("Error deleting discharge summary:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete discharge summary"
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!selectedRecord?._id) return;

    try {
      await deleteDischargeSummary(selectedRecord._id);
      // Close modal and refresh data after successful delete
      setShowDischargeSummaryModal(false);
      resetDischargeSummaryForm();
      fetchAppointments();
      setShowDeleteConfirmModal(false);
    } catch (error) {
      // Error already handled in deleteDischargeSummary
      setShowDeleteConfirmModal(false);
    }
  };

  // Discharge Summary PDF Generation
  const generateDischargeSummaryPDF = (record) => {
    if (!record) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const headerHeight = 25;
    const footerHeight = 25;
    let yPos = headerHeight + 10;

    // Helper function to add header and footer
    const addHeaderFooter = () => {
      if (headerImage) {
        doc.addImage(headerImage, "PNG", 0, 0, pageWidth, headerHeight);
      }
      if (footerImage) {
        doc.addImage(
          footerImage,
          "PNG",
          0,
          pageHeight - footerHeight,
          pageWidth,
          footerHeight
        );
      }
    };

    // Helper function to split text into lines that fit the page width
    const splitText = (text, maxWidth) => {
      if (!text) return [];
      const lines = doc.splitTextToSize(text, maxWidth - margin * 2);
      return lines;
    };

    // Helper function to add text with automatic page breaks
    const addTextWithPageBreak = (text, x, y, maxWidth, fontSize = 10) => {
      doc.setFontSize(fontSize);
      const lines = splitText(text, maxWidth);
      let currentY = y;

      lines.forEach((line) => {
        if (currentY > pageHeight - footerHeight - 10) {
          doc.addPage();
          addHeaderFooter();
          currentY = headerHeight + 10;
        }
        doc.text(line, x, currentY);
        currentY += fontSize * 0.5;
      });

      return currentY;
    };

    // PAGE 1: Patient and Admission Details
    addHeaderFooter();

    // Title
    doc.setFontSize(18);
    doc.setTextColor(220, 20, 60); // Red color
    doc.text("DISCHARGE SUMMARY", margin, yPos);
    yPos += 10;

    // Discharge Type box
    doc.setFillColor(200, 200, 200);
    doc.rect(margin, yPos, pageWidth - margin * 2, 8, "F");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    const dischargeTypeLabels = {
      discharge_on_request: "Discharge on Request",
      discharge_against_medical_advice: "Discharge Against Medical Advice",
      left_against_medical_advice: "Left Against Medical Advice",
      normal: "Normal Discharge",
    };
    const dischargeTypeText = dischargeTypeLabels[dischargeType] || "";
    doc.text(dischargeTypeText, margin + 2, yPos + 5);
    yPos += 15;

    // Patient details
    doc.setFontSize(10);
    const patient = record.patient;

    doc.text(`Bed : ${record.bedNumber || ""}`, margin, yPos);
    const regNoX = pageWidth / 2 + 20;
    doc.text(
      `Reg. No. : ${regNumber || patient.patientId || ""}`,
      regNoX,
      yPos
    );
    yPos += 7;

    doc.text(`Name : ${patient.name || ""}`, margin, yPos);
    yPos += 7;

    const addressToUse = patientAddress || patient.address || "";
    doc.text(`Address : ${addressToUse}`, margin, yPos);
    yPos += 7;

    const addressLines = splitText(addressToUse, pageWidth - margin * 2);
    if (addressLines.length > 1) {
      yPos += (addressLines.length - 1) * 4;
    }

    doc.text(`Age : ${patient.age || ""}`, margin, yPos);
    doc.text(`Sex : ${patient.gender || ""}`, regNoX, yPos);
    yPos += 7;

    const admissionDateStr = record.admissionDate
      ? dayjs(record.admissionDate).format("DD/MM/YYYY")
      : "";
    doc.text(`Admission Date : ${admissionDateStr}`, margin, yPos);
    yPos += 7;

    const dischargeDateStr = record.dischargedDate
      ? dayjs(record.dischargedDate, "DD-MM-YYYY").format("DD/MM/YYYY")
      : dayjs().format("DD/MM/YYYY");
    doc.text(`Discharge Date : ${dischargeDateStr}`, margin, yPos);
    yPos += 10;

    // Consultant section
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Consultant :", margin, yPos);
    yPos += 7;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    // Display multiple consultants
    console.log('PDF Generation - Consultants:', consultants);
    if (consultants && consultants.length > 0) {
      consultants.forEach((consultant, index) => {
        if (yPos > pageHeight - footerHeight - 10) {
          doc.addPage();
          addHeaderFooter();
          yPos = headerHeight + 10;
        }
        doc.text(`${index + 1}) ${consultant}`, margin + 5, yPos);
        yPos += 6;
      });
    } else {
      // Fallback to empty if no consultants added
      doc.text("1) ", margin + 5, yPos);
      yPos += 6;
    }

    // Diagnosis section
    yPos += 5;
    doc.setFont("helvetica", "bold");
    doc.text("Diagnosis :", margin, yPos);
    yPos += 7;
    doc.setFont("helvetica", "normal");

    // Display multiple diagnoses
    if (diagnoses.length > 0) {
      diagnoses.forEach((diagnosis, index) => {
        if (yPos > pageHeight - footerHeight - 10) {
          doc.addPage();
          addHeaderFooter();
          yPos = headerHeight + 10;
        }
        doc.text(`${index + 1}) ${diagnosis}`, margin + 5, yPos);
        yPos += 6;
      });
    } else {
      // Fallback: Get diagnosis from treatments if no diagnoses added
      const fallbackDiagnoses = treatmentsData
        .map((t) => t.complaints)
        .filter((d) => d)
        .slice(0, 6);
      if (fallbackDiagnoses.length > 0) {
        fallbackDiagnoses.forEach((diagnosis, index) => {
          if (yPos > pageHeight - footerHeight - 10) {
            doc.addPage();
            addHeaderFooter();
            yPos = headerHeight + 10;
          }
          doc.text(`${index + 1}) ${diagnosis}`, margin + 5, yPos);
          yPos += 6;
        });
      } else {
        // Empty diagnosis
        doc.text("1) ", margin + 5, yPos);
        yPos += 6;
      }
    }

    // PAGE 2: Clinical Summary and Investigations
    doc.addPage();
    addHeaderFooter();
    yPos = headerHeight + 10;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(25, 135, 84); // Green color
    doc.text("CLINICAL SUMMARY", margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    yPos = addTextWithPageBreak(
      clinicalSummary || "No clinical summary provided.",
      margin,
      yPos,
      pageWidth
    );
    yPos += 10;

    // Investigations
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(25, 135, 84);
    doc.text("INVESTIGATIONS", margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    yPos = addTextWithPageBreak(
      investigations || "No investigations provided.",
      margin,
      yPos,
      pageWidth
    );

    // PAGE 3: Brief Review To Treatment
    doc.addPage();
    addHeaderFooter();
    yPos = headerHeight + 10;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(25, 135, 84);
    doc.text("BRIEF REVIEW TO TREATMENT", margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    yPos = addTextWithPageBreak(
      treatmentReview || "No treatment review provided.",
      margin,
      yPos,
      pageWidth
    );

    // PAGE 4: Treatment Advice, Remarks, Follow-up and Consultant
    doc.addPage();
    addHeaderFooter();
    yPos = headerHeight + 10;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(25, 135, 84);
    doc.text("TREATMENT ADVICE", margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    yPos = addTextWithPageBreak(
      treatmentAdvice || "No treatment advice provided.",
      margin,
      yPos,
      pageWidth
    );
    yPos += 10;

    // Remarks
    if (remarks) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(25, 135, 84);
      doc.text("REMARKS", margin, yPos);
      yPos += 10;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      yPos = addTextWithPageBreak(remarks, margin, yPos, pageWidth);
      yPos += 10;
    }

    // Follow-up Date and Consultant In Charge at bottom
    const bottomY = pageHeight - footerHeight - 20;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");

    if (dischargeFollowUpDate) {
      const followUpStr = dayjs(dischargeFollowUpDate).format("DD-MM-YYYY");
      doc.text(`Follow-up Date : ${followUpStr}`, margin, bottomY);
    }

    // Show first consultant as "Consultant In Charge"
    if (consultants.length > 0) {
      const consultantX = pageWidth / 2;
      doc.text(
        `Consultant In Charge : ${consultants[0]}`,
        consultantX,
        bottomY
      );
    }

    // Save PDF
    const patientName = patient.name
      ? patient.name.replace(/\s+/g, "_")
      : "Patient";
    const fileName = `Discharge_Summary_${patientName}_${Date.now()}.pdf`;
    doc.save(fileName);

    toast.success("Discharge Summary PDF generated successfully!");
  };

  const closeViewInvoiceModal = () => {
    setIsViewInvoiceModalOpen(false);
    setSelectedAppointmentId(null);
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}get-all-admission?patientId=${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const allRecords = res.data.result || [];
      const discharged = allRecords.filter(
        (item) => item.status === "discharged"
      );
      setDischargedRecords(discharged);
    } catch (err) {
      console.error("Failed to fetch appointments:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const generateMedicalCertificate = async (record) => {
    if (!record || !record.patient) {
      toast.error("Patient data not available");
      return;
    }

    try {
      // Fetch aggregated treatment records
      const treatmentsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}get-all-treatment?admissionId=${record._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const treatmentsData = treatmentsRes.data.result || [];
      const admission = record;
      const patient = record.patient || {};
      const name = patient.name || "";

      const gender = (patient.gender || "").toLowerCase();
      // const pronoun = gender.startsWith("f") ? "She" : "He";
      let pronoun;
      if (gender.startsWith("f")) {
        pronoun = "She"
      }
      else if (gender.startsWith("m")) {
        pronoun = "He"
      }
      else {
        pronoun = "They"
      }

      // Extract all treatment names from observations
      let treatmentsList = [];
      if (treatmentsData.length > 0) {
        treatmentsData.forEach(item => {
          if (item.treatments && Array.isArray(item.treatments)) {
            item.treatments.forEach(t => {
              if (t.name && !treatmentsList.includes(t.name)) {
                treatmentsList.push(t.name);
              }
            });
          }
        });
      }

      // Get suffering from (reason for admission)
      const sufferingFrom = admission.reason || "treatment";

      // Get dates
      const fromDateStr = admission.admissionDate
        ? dayjs(admission.admissionDate).format("DD/MM/YYYY")
        : dayjs().format("DD/MM/YYYY");
      const toDateStr = admission.dischargedDate
        ? dayjs(admission.dischargedDate, "DD-MM-YYYY").format("DD/MM/YYYY")
        : dayjs().format("DD/MM/YYYY");

      // Generate PDF
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const headerHeight = 25;
      const footerHeight = 25;
      let yPos = headerHeight + 20;

      // ===== HEADER & FOOTER =====
      if (headerImage) {
        doc.addImage(headerImage, "PNG", 0, 0, pageWidth, headerHeight);
      }
      if (footerImage) {
        doc.addImage(
          footerImage,
          "PNG",
          0,
          pageHeight - footerHeight,
          pageWidth,
          footerHeight
        );
      }

      // ===== TITLE =====
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      const hospitalTitle = "ASHTANG AYURVED HOSPITAL";
      const titleWidth = doc.getTextWidth(hospitalTitle);
      doc.text(hospitalTitle, (pageWidth - titleWidth) / 2, yPos);
      yPos += 12;

      // Box: MEDICAL CERTIFICATE
      const boxWidth = 70;
      const boxHeight = 10;
      const boxX = (pageWidth - boxWidth) / 2;
      doc.rect(boxX, yPos, boxWidth, boxHeight);
      doc.setFontSize(12);
      const certText = "MEDICAL CERTIFICATE";
      const certTextWidth = doc.getTextWidth(certText);
      doc.text(certText, (pageWidth - certTextWidth) / 2, yPos + 6.5);
      yPos += 18;

      // ===== MAIN BODY =====
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      const centerX = pageWidth / 2;
      doc.text("To whomever it may concern,", centerX, yPos, { align: "center" });
      yPos += 12;

      const wrap = (text) =>
        doc.splitTextToSize(text, pageWidth - margin * 2);

      // 1) This is to certify...
      const para1 = `This is to certify that ${name} is advised for panchakarma and physiotherapy treatment under medical observation.`;
      wrap(para1).forEach((line) => {
        doc.text(line, margin, yPos);
        yPos += 6;
      });
      yPos += 2;

      // 2) Suffering from
      const para2 = `${pronoun} is suffering from  ${sufferingFrom}.`;
      wrap(para2).forEach((line) => {
        doc.text(line, margin, yPos);
        yPos += 6;
      });
      yPos += 2;

      // 3) Under treatment & rest
      const para3 = `${pronoun} is under treatment and is advised to take rest.`;
      wrap(para3).forEach((line) => {
        doc.text(line, margin, yPos);
        yPos += 6;
      });
      yPos += 2;

      // 4) From–to–for
      const para4 = `${pronoun} was under treatment from  ${fromDateStr}  to  ${toDateStr}  for ${sufferingFrom}.`;
      wrap(para4).forEach((line) => {
        doc.text(line, margin, yPos);
        yPos += 6;
      });
      yPos += 10;

      // ===== FOLLOWING TREATMENT LIST =====
      if (treatmentsList.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.text("Following treatment was done :-", margin, yPos);
        yPos += 8;

        doc.setFont("helvetica", "normal");

        const leftX = margin;
        const rightX = pageWidth / 2 + 5;
        let leftY = yPos;
        let rightY = yPos;

        treatmentsList.forEach((t, index) => {
          const label = `${index + 1}) ${t}`;
          const isLeft = index % 2 === 0;
          if (isLeft) {
            doc.text(label, leftX, leftY);
            leftY += 6;
          } else {
            doc.text(label, rightX, rightY);
            rightY += 6;
          }
        });
      }

      // ===== SAVE PDF =====
      const safeName = (name || "Patient").replace(/\s+/g, "_");
      doc.save(`Medical_Certificate_${safeName}.pdf`);

      toast.success("Medical certificate generated successfully!");
    } catch (error) {
      console.error("Error generating medical certificate:", error);
      toast.error("Failed to generate medical certificate");
    }
  };

  return (
    <div className="ipd-list-container">
      <h2 className="ipd-list-title">
        <FaHospital /> Discharged Admissions
      </h2>
      {loading ? (
        <SkeletonLoader variant="discharged-list" count={3} />
      ) : dischargedRecords.length === 0 ? (
        <p>No discharged records found.</p>
      ) : (
        <div className="ipd-list-records-wrapper">
          {dischargedRecords.map((record) => (
            <div
              key={record._id}
              className="ipd-list-record-card"
            >
              <div className="ipd-list-record-header">
                <h3 className="ipd-list-record-name">
                  {record.patient.name}
                </h3>
                <span className="ipd-list-status-badge">
                  <FaCheckCircle /> {record.status.toUpperCase()}
                </span>
              </div>

              <hr className="ipd-list-divider" />

              <div className="ipd-list-info-grid">
                <p className="ipd-list-info-item">
                  <strong className="ipd-list-info-label">
                    <FaUser /> Gender:
                  </strong> {record.patient.gender}
                </p>
                <p className="ipd-list-info-item">
                  <strong className="ipd-list-info-label">
                    <FaCalendarAlt /> Age:
                  </strong> {record.patient.age}
                </p>
                <p className="ipd-list-info-item">
                  <strong className="ipd-list-info-label">
                    <FaPhone className="ipd-list-phone-icon" /> Contact:
                  </strong> {record.patient.contact}
                </p>
                <p className="ipd-list-info-item">
                  <strong className="ipd-list-info-label">
                    <FaCalendarAlt /> Admission:
                  </strong>{" "}
                  {dayjs(record.admissionDate).format("DD/MM/YYYY")}
                </p>
                {(record.roomNumber !== null &&
                  record.roomNumber !== undefined &&
                  String(record.roomNumber).trim() !== "") && (
                  <p className="ipd-list-info-item">
                    <strong className="ipd-list-info-label">
                      <FaDoorOpen /> Room:
                    </strong>{" "}
                    {record.roomNumber}
                    {/* testing comment */}
                  </p>
                )}
                {(record.bedNumber !== null &&
                  record.bedNumber !== undefined &&
                  String(record.bedNumber).trim() !== "") && (
                  <p className="ipd-list-info-item">
                    <strong className="ipd-list-info-label">
                      <FaBed /> Bed:
                    </strong>{" "}
                    {record.bedNumber}
                  </p>
                )}
              </div>

              <div className="ipd-list-reason-section">
                <p className="ipd-list-info-item">
                  <strong className="ipd-list-info-label">
                    <FaFileAlt /> Reason:
                  </strong> {record.reason}
                </p>
              </div>

              <div className="ipd-list-actions-wrapper">
                {(() => {
                  const legacyInvoice = record.invoice && !record.invoice.invoiceType && !record.medicineInvoice;

                  if (legacyInvoice) {
                    return (
                      <button
                        className="ipd-list-button ipd-list-button-secondary"
                        onClick={() => openViewInvoiceModal(record._id, true)}
                      >
                        View Invoice
                      </button>
                    );
                  }

                  return (
                    <>
                      {record.invoice?.invoiceType === "treatment" ? (
                        <button
                          className="ipd-list-button ipd-list-button-secondary"
                          onClick={() => openViewInvoiceModal(record._id, true, "treatment")}
                        >
                          View Treatment Invoice
                        </button>
                      ) : (
                        <button
                          className="ipd-list-button ipd-list-button-secondary"
                          onClick={() => openInvoiceModal(record, true, "treatment")}
                        >
                          Create Treatment Invoice
                        </button>
                      )}
                      {record.medicineInvoice ? (
                        <button
                          className="ipd-list-button ipd-list-button-secondary"
                          onClick={() => openViewInvoiceModal(record._id, true, "medicine")}
                        >
                          View Medicine Invoice
                        </button>
                      ) : (
                        <button
                          className="ipd-list-button ipd-list-button-secondary"
                          onClick={() => openInvoiceModal(record, true, "medicine")}
                        >
                          Create Medicine Invoice
                        </button>
                      )}
                    </>
                  );
                })()}
                <button
                  className="ipd-list-button ipd-list-button-secondary ipd-list-summary-button"
                  onClick={() => openDischargeSummaryModal(record)}
                >
                  <FaFileAlt /> {record.isDischargeCardGenerated ? "Show Summary" : "Create Summary"}
                </button>
                <button
                  className={`ipd-list-button ipd-list-button-secondary ipd-list-certificate-button ${(record.invoice || record.medicineInvoice) ? 'ipd-list-certificate-button-enabled' : 'ipd-list-certificate-button-disabled'}`}
                  onClick={() => {
                    if (!record.invoice && !record.medicineInvoice) {
                      toast.error("Please generate invoice first");
                      return;
                    }
                    generateMedicalCertificate(record);
                  }}
                >
                  <FaFileAlt /> Medical Certificate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {isInvoiceModalOpen && selectedConsultation && (
        <InvoiceModal
          consultation={selectedConsultation}
          onClose={closeInvoiceModal}
          patientId={patientId}
          billType={selectedBillType}
          refreshConsultations={fetchAppointments}
        />
      )}
      {isViewInvoiceModalOpen && selectedAppointmentId && (
        <ViewInvoiceModal
          appointmentId={selectedAppointmentId}
          isIPD={isSelectedIPD}
          billType={selectedBillType}
          onClose={closeViewInvoiceModal}
        />
      )}

      {/* Discharge Summary Modal */}
      {showDischargeSummaryModal && selectedRecord && (
        <div className="ipd-list-modal-overlay">
          <div className="ipd-list-modal ipd-list-modal-large">
            <div className="ipd-list-modal-header ipd-list-modal-header-flex">
              <div className="ipd-list-modal-header-flex-container">
                <h3>{selectedRecord?.isDischargeCardGenerated ? "Edit Discharge Summary" : "Create Discharge Summary"}</h3>
                {/* Step Indicator in Header */}
                <div className="ipd-list-step-indicator ipd-list-step-indicator-container">
                  {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
                    <div
                      key={step}
                      className={`ipd-list-step-indicator-circle ${currentStep >= step ? 'ipd-list-step-indicator-circle-active' : 'ipd-list-step-indicator-circle-inactive'}`}
                      onClick={() => setCurrentStep(step)}
                      title={`Step ${step}`}
                    >
                      {step}
                    </div>
                  ))}
                </div>
              </div>
              <div className="ipd-list-modal-actions">
                {/* Delete button - only show when viewing existing summary */}
                {selectedRecord?.isDischargeCardGenerated && (
                  <button
                    className="ipd-list-modal-close ipd-list-delete-button"
                    onClick={() => setShowDeleteConfirmModal(true)}
                    title="Delete Discharge Summary"
                    disabled={loading || loadingDischargeCard}
                    style={{width:"20px", height:"20px"}}
                  >
                    <FaTrashAlt />
                  </button>
                )}
                <button
                  className="ipd-list-modal-close"
                  onClick={() => {
                    setShowDischargeSummaryModal(false);
                    resetDischargeSummaryForm();
                  }}
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="ba-modal-body ipd-list-modal-body-discharge">
              {loadingDischargeCard ? (
                <div className="ipd-list-loading-container">
                  <div className="ipd-list-spinner"></div>
                  <p className="ipd-list-loading-text">Loading discharge summary...</p>
                </div>
              ) : (
                <>
                  {/* Step 1: Basic Details until Diagnosis */}
                  {currentStep === 1 && (
                    <div className="ipd-list-step-content ipd-list-step-content-step1">
                      <h4 className="ipd-list-step-heading-step1">Step 1: Basic Information</h4>

                      {/* Discharge Type and Reg. No. */}
                      <div className="ipd-list-form-row">
                        <div className="ipd-list-input-group">
                          <label className="ipd-list-label">Type of Discharge</label>
                          <CustomDropdown
                            label="Select Discharge Type"
                            value={dischargeType}
                            onChange={(value) => setDischargeType(value)}
                            options={[
                              {
                                value: "discharge_on_request",
                                label: "Discharge on Request",
                              },
                              {
                                value: "discharge_against_medical_advice",
                                label: "Discharge Against Medical Advice",
                              },
                              {
                                value: "left_against_medical_advice",
                                label: "Left Against Medical Advice",
                              },
                              { value: "normal", label: "Normal Discharge" },
                            ]}
                          />
                        </div>

                        <div className="ipd-list-input-group">
                          <label className="ipd-list-label">Reg. No.</label>
                          <input
                            className="ipd-list-input"
                            placeholder="Enter Registration Number"
                            value={regNumber}
                            onChange={(e) => setRegNumber(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="ipd-list-input-group">
                        <label className="ipd-list-label">Address</label>
                        <textarea
                          className="ipd-list-textarea"
                          rows={3}
                          placeholder="Enter patient address"
                          value={patientAddress}
                          onChange={(e) => setPatientAddress(e.target.value)}
                        />
                      </div>

                      <div className="ipd-list-input-group">
                        <label className="ipd-list-label">Patient History</label>
                        <textarea
                          className="ipd-list-textarea"
                          rows={3}
                          placeholder="Enter patient history"
                          value={patientHistory}
                          onChange={(e) => setPatientHistory(e.target.value)}
                        />
                      </div>

                      {/* Consultants Section */}
                      <div className="ipd-list-section">
                        <h3 className="ipd-list-section-title">Consultants</h3>
                        <div className="ipd-list-medicine-inputs">
                          <input
                            className="ipd-list-input"
                            placeholder="Enter consultant name"
                            value={consultantName}
                            onChange={(e) => setConsultantName(e.target.value)}
                          />
                          <button
                            className="ipd-list-button ipd-list-button-secondary"
                            onClick={() => {
                              if (consultantName.trim()) {
                                setConsultants([...consultants, consultantName.trim()]);
                                setConsultantName("");
                              }
                            }}
                          >
                            Add Consultant
                          </button>
                        </div>
                        {consultants.length > 0 && (
                          <div className="ba-medicines-list-container ipd-list-medicines-list-margin">
                            <ul className="ipd-list-medicines-list">
                              {consultants.map((consultant, index) => (
                                <li
                                  key={index}
                                  className="ba-medicine-item ipd-list-medicine-item-flex"
                                >
                                  <span className="ipd-list-medicine-name">
                                    {index + 1}) {consultant}
                                  </span>
                                  <button
                                    className="ipd-list-remove-button"
                                    onClick={() =>
                                      setConsultants(
                                        consultants.filter((_, i) => i !== index)
                                      )
                                    }
                                    title="Remove consultant"
                                  >
                                    <FaTrashAlt />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Diagnosis Section */}
                      <div className="ipd-list-section">
                        <h3 className="ipd-list-section-title">Diagnosis</h3>
                        <div className="ipd-list-medicine-inputs">
                          <input
                            className="ipd-list-input"
                            placeholder="Enter diagnosis"
                            value={diagnosisText}
                            onChange={(e) => setDiagnosisText(e.target.value)}
                          />
                          <button
                            className="ipd-list-button ipd-list-button-secondary"
                            onClick={() => {
                              if (diagnosisText.trim()) {
                                setDiagnoses([...diagnoses, diagnosisText.trim()]);
                                setDiagnosisText("");
                              }
                            }}
                          >
                            Add Diagnosis
                          </button>
                        </div>
                        {diagnoses.length > 0 && (
                          <div className="ba-medicines-list-container ipd-list-medicines-list-margin">
                            <ul className="ipd-list-medicines-list">
                              {diagnoses.map((diagnosis, index) => (
                                <li
                                  key={index}
                                  className="ba-medicine-item ipd-list-medicine-item-flex"
                                >
                                  <span className="ipd-list-medicine-name">
                                    {index + 1}) {diagnosis}
                                  </span>
                                  <button
                                    className="ipd-list-remove-button"
                                    onClick={() =>
                                      setDiagnoses(
                                        diagnoses.filter((_, i) => i !== index)
                                      )
                                    }
                                    title="Remove diagnosis"
                                  >
                                    <FaTrashAlt />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Clinical Summary */}
                  {currentStep === 2 && (
                    <div className="ipd-list-step-content ipd-list-step-content-steps">
                      <h4 className="ipd-list-step-heading-steps">Step 2: Clinical Summary</h4>
                      <div className="ba-input-group ipd-list-step-input-group">
                        <label className="ba-label ipd-list-step-label">Clinical Summary</label>
                        <textarea
                          className="ba-textarea ipd-list-step-textarea"
                          placeholder="Enter clinical summary of the patient's condition..."
                          value={clinicalSummary}
                          onChange={(e) => setClinicalSummary(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Investigations */}
                  {currentStep === 3 && (
                    <div className="ipd-list-step-content ipd-list-step-content-steps">
                      <h4 className="ipd-list-step-heading-steps">Step 3: Investigations</h4>
                      <div className="ba-input-group ipd-list-step-input-group">
                        <label className="ba-label ipd-list-step-label">Investigations</label>
                        <textarea
                          className="ba-textarea ipd-list-step-textarea"
                          placeholder="Enter investigations performed and results..."
                          value={investigations}
                          onChange={(e) => setInvestigations(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 4: Brief Review To Treatment */}
                  {currentStep === 4 && (
                    <div className="ipd-list-step-content ipd-list-step-content-steps">
                      <h4 className="ipd-list-step-heading-steps">Step 4: Brief Review To Treatment</h4>
                      <div className="ba-input-group ipd-list-step-input-group">
                        <label className="ba-label ipd-list-step-label">Brief Review To Treatment</label>
                        <textarea
                          className="ba-textarea ipd-list-step-textarea"
                          placeholder="Enter brief review of treatment provided..."
                          value={treatmentReview}
                          onChange={(e) => setTreatmentReview(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 5: Treatment Advice */}
                  {currentStep === 5 && (
                    <div className="ipd-list-step-content ipd-list-step-content-steps">
                      <h4 className="ipd-list-step-heading-steps">Step 5: Treatment Advice</h4>
                      <div className="ba-input-group ipd-list-step-input-group">
                        <label className="ba-label ipd-list-step-label">Treatment Advice</label>
                        <textarea
                          className="ba-textarea ipd-list-step-textarea"
                          placeholder="Enter treatment advice for discharge..."
                          value={treatmentAdvice}
                          onChange={(e) => setTreatmentAdvice(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 6: Remarks & Follow-up Date */}
                  {currentStep === 6 && (
                    <div className="ipd-list-step-content ipd-list-step-content-steps">
                      <h4 className="ipd-list-step-heading-steps">Step 6: Remarks & Follow-up</h4>
                      <div className="ba-input-group ipd-list-step-input-group-remarks">
                        <label className="ba-label ipd-list-step-label">Remarks</label>
                        <textarea
                          className="ba-textarea ipd-list-step-textarea-remarks"
                          placeholder="Enter any additional remarks..."
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          rows={5}
                        />
                      </div>
                      <div className="ba-form-row ipd-list-step-form-row">
                        <div className="ipd-list-input-group">
                          <label className="ipd-list-label">Follow-up Date</label>
                          <CustomDatePicker
                            label="Select Follow-up Date"
                            value={dischargeFollowUpDate ? (dayjs.isDayjs(dischargeFollowUpDate) ? dischargeFollowUpDate : dayjs(dischargeFollowUpDate)) : null}
                            onChange={setDischargeFollowUpDate}
                            minDate={dayjs().startOf("day")}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="ipd-list-modal-footer">
              {/* Cancel button - always visible */}

              {/* Navigation buttons - show on all steps except last */}
              {currentStep < totalSteps && (
                <>
                  {currentStep > 1 && (
                    <button
                      className="ipd-list-button ipd-list-button-secondary"
                      onClick={previousStep}
                      disabled={loadingDischargeCard}
                    >
                      Previous
                    </button>
                  )}
                  <button
                    className="ipd-list-button ipd-list-button-primary"
                    onClick={nextStep}
                    disabled={loadingDischargeCard}
                  >
                    Next
                  </button>
                </>
              )}

              {/* Action buttons - only show on last step */}
              {currentStep === totalSteps && (
                <>
                  {/* Show Download PDF button when viewing existing summary */}
                  {selectedRecord?.isDischargeCardGenerated && (
                    <button
                      className="ipd-list-button ba-button-primary"
                      onClick={() => {
                        generateDischargeSummaryPDF(selectedRecord);
                      }}
                      disabled={loadingDischargeCard}
                    >
                      Download PDF
                    </button>
                  )}

                  {/* Show Update button only if viewing existing summary */}
                  {selectedRecord?.isDischargeCardGenerated && (
                    <button
                      className="ipd-list-button ipd-list-button-secondary"
                      onClick={async () => {
                        try {
                          await saveOrUpdateDischargeSummary(selectedRecord);
                          // Update original data after successful update
                          setOriginalDischargeCardData(null);
                          // Close modal and refresh data after successful update
                          setShowDischargeSummaryModal(false);
                          resetDischargeSummaryForm();
                          fetchAppointments();
                        } catch (error) {
                          // Error already handled in saveOrUpdateDischargeSummary
                        }
                      }}
                      disabled={loading || loadingDischargeCard}
                    >
                      {loading ? "Updating..." : "Update"}
                    </button>
                  )}

                  {/* Show Update & Generate PDF button only if viewing existing summary and has changes */}
                  {selectedRecord?.isDischargeCardGenerated && hasFormDataChanged() && (
                    <button
                      className="ipd-list-button ba-button-primary"
                      onClick={async () => {
                        try {
                          // Update first, then generate PDF
                          await saveOrUpdateDischargeSummary(selectedRecord);
                          // Update original data after successful update
                          setOriginalDischargeCardData(null);
                          generateDischargeSummaryPDF(selectedRecord);
                          // Close modal and refresh data after successful update and PDF generation
                          setShowDischargeSummaryModal(false);
                          resetDischargeSummaryForm();
                          fetchAppointments();
                        } catch (error) {
                          // If update fails, still allow PDF generation
                          generateDischargeSummaryPDF(selectedRecord);
                        }
                      }}
                      disabled={loading || loadingDischargeCard}
                    >
                      {loading ? "Updating..." : "Update & Generate PDF"}
                    </button>
                  )}

                  {/* Show Save button only if creating new summary */}
                  {!selectedRecord?.isDischargeCardGenerated && (
                    <button
                      className="ipd-list-button ipd-list-button-secondary"
                      onClick={async () => {
                        try {
                          await saveOrUpdateDischargeSummary(selectedRecord);
                          // Close modal and refresh data after successful save
                          setShowDischargeSummaryModal(false);
                          resetDischargeSummaryForm();
                          fetchAppointments();
                        } catch (error) {
                          // Error already handled in saveOrUpdateDischargeSummary
                        }
                      }}
                      disabled={loading || loadingDischargeCard}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  )}

                  {/* Show Save & Generate PDF button only if creating new summary */}
                  {!selectedRecord?.isDischargeCardGenerated && (
                    <button
                      className="ipd-list-button ba-button-primary"
                      onClick={async () => {
                        try {
                          // Save first, then generate PDF
                          await saveOrUpdateDischargeSummary(selectedRecord);
                          generateDischargeSummaryPDF(selectedRecord);
                          // Close modal and refresh data after successful save and PDF generation
                          setShowDischargeSummaryModal(false);
                          resetDischargeSummaryForm();
                          fetchAppointments();
                        } catch (error) {
                          // If save fails, still allow PDF generation
                          generateDischargeSummaryPDF(selectedRecord);
                        }
                      }}
                      disabled={loading || loadingDischargeCard}
                    >
                      {loading ? "Saving..." : "Save & Generate PDF"}
                    </button>
                  )}

                  {/* Previous button on last step */}
                  {currentStep > 1 && (
                    <button
                      className="ipd-list-button ipd-list-button-secondary"
                      onClick={previousStep}
                      disabled={loadingDischargeCard}
                    >
                      Previous
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        open={showDeleteConfirmModal}
        onClose={() => {
          setShowDeleteConfirmModal(false);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Discharge Summary"
        message="Are you sure you want to delete this discharge summary? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonAppearance="primary"
        isDelete={true}
      />

    </div>
  );
}

export default IpdList;
